/**
 * 拆分设备管理模块
 */

/**
 * 设备类
 */
export interface Equipment{
    id:number|0
    name:string
    number:string
    dtuNumber:string
    equipmentTypeId:number|0
    equipmentGroupId:number|0
}

/**
 * 设备分组查询
 */
export interface EquipmentGroup{
  id:number|0
  name:string,
  accountId?:number|0
  userId?:number|0
}

/**
 * 设备类型
 */
export interface EquipmentType{
    id:number|0
    name:string
}

/**
 * 设备统计的返回对象
 */
export interface EquipmentStatistic{
  totalEquipment:number | 0
  OnlineEquipment:number | 0
  OfflineEquipment:number | 0
  FaultEquipment:number | 0
  ShortageEquipment:number | 0
}

export interface PageResult<T> {
    data: T[]
    total: number
    pageIndex: number
    pageSize: number
}

/**
 * 通用响应结果
 */
export interface ApiResponse<T = any> {
    code: string | number
    message: string
    data: T
    success: boolean,
    totalCount:0,
}

/**
 * 设备服务
 */
export class EquipmentService{
    private baseUrl:string = "api/ecs/equipment"
    private httpService: any

    // 构造函数，初始化http请求服务
    constructor(httpService?: any) {
        this.httpService = httpService
    }

    /**
     * 创建服务实例（便捷方法）
     */
    static create(httpService?: any): EquipmentService {
        return new EquipmentService(httpService)
    }

    /**
     * 获取设备列表
     * @param params
     */
    async getEquipmentList(params?:{
        pageIndex?:number
        pageSize?:number
        name?:string
        number?:string
        fixedAssetNumber?:string
        dtuNumber?:string
        groupIds?:number[]
        filterFlag?:number|0
    }):Promise<ApiResponse<PageResult<Equipment>>>{
        const http = this.httpService || this.getHttpService()
        if (!http) {
            return this.handleError(new Error('HTTP服务未初始化'))
        }

        try{
            const queryParams: Record<string, any> = {
                pageIndex: (params && params.pageIndex) || 1,
                pageSize: (params && params.pageSize) || 10,
                isFullLoad:true // load navigation parameters
            }

            if (params && params.name) queryParams.name = params.name
            if (params && params.number) queryParams.number = params.number
            if (params && params.dtuNumber) queryParams.dtuNumber = params.dtuNumber
            if (params && params.fixedAssetNumber) queryParams.fixedAssetNumber = params.fixedAssetNumber
            if (params && params.groupIds && params.groupIds.length) queryParams.groupIds = params.groupIds.join(',')
            if (params && params.filterFlag) queryParams.filterFlag = params.filterFlag

            // call endpoint
            const response = await http.get(`${this.baseUrl}`,queryParams)
            return this.handleResponse(response)

        }catch (err){
            return this.handleError(err)
        }
    }

    /**
     * 创建设备
     * @param equipment
     */
    async createEquipment(equipment:Equipment):Promise<ApiResponse<Equipment>>{
        const http = this.httpService || this.getHttpService()
        if (!http) {
            return this.handleError(new Error('HTTP服务未初始化'))
        }
        try{
            const response = await http.post(`${this.baseUrl}`,equipment)
            return this.handleResponse(response)
        }catch (err){
            return this.handleError(err)
        }
    }

    /**
     * 修改设备
     * @param equipment
     */
    async updateEquipment(equipment:Equipment){
        const http = this.httpService || this.getHttpService()
        if (!http) {
            return this.handleError(new Error('HTTP服务未初始化'))
        }

        try{
            const response = await http.put(`${this.baseUrl}`,equipment)
            return this.handleResponse(response)
        }catch(err){
            return this.handleError(err)
        }
    }

  /**
   * 删除设备
   * @param id
   */
  async deleteEquipment(id:number){
      const http = this.httpService || this.getHttpService()
      if (!http) {
        return this.handleError(new Error('HTTP服务未初始化'))
      }

      try{
        const response = await http.delete(`${this.baseUrl}/${id}`)
        return this.handleResponse(response)
      }catch (err){
        return this.handleError(err)
      }
    }

  /**
   * 异步查询不同状态的设备数量
   */
  async statisticEquipments():Promise<ApiResponse<EquipmentStatistic>>{
      const http = this.httpService || this.getHttpService()
      if (!http) {
        return this.handleError(new Error('HTTP服务未初始化'))
      }

      try{
        // call
        const response = await http.get(`${this.baseUrl}/statistic`)
        return this.handleResponse(response)
      }catch(err){
        return this.handleError(err)
      }
    }
    //==================================================================================================================
  /**
   * 获取设备类型
   */
  async getEquipmentTypes():Promise<ApiResponse<PageResult<EquipmentType>>>{
    const http = this.httpService || this.getHttpService()
    if (!http) {
      return this.handleError(new Error('HTTP服务未初始化'))
    }

    try{
      const response = await http.get(`${this.baseUrl}/types`)
      return this.handleResponse(response)
    }catch(err){
      return this.handleError(err)
    }
  }


  async getEquipmentGroup():Promise<ApiResponse<EquipmentGroup[]>>{
    const http = this.httpService || this.getHttpService()
    if (!http) {
      return this.handleError(new Error('HTTP服务未初始化'))
    }

    try{
      const response = await http.get(`${this.baseUrl}/group`)
      return this.handleResponse(response)
    }catch(err){
      return this.handleError(err)
    }
  }

  async createEquipmentGroup(group: {
    name: string
    userId: number | 0
  }): Promise<ApiResponse<EquipmentGroup>> {
    const http = this.httpService || this.getHttpService()
    if (!http) {
      return this.handleError(new Error('HTTP服务未初始化'))
    }

    try {
      const response = await http.post(`${this.baseUrl}/group`, group)
      return this.handleResponse(response)
    } catch (err) {
      return this.handleError(err)
    }
  }

  async updateEquipmentGroup(group: {
    id: number | 0
    name: string
    userId: number | 0
  }): Promise<ApiResponse<EquipmentGroup>> {
    const http = this.httpService || this.getHttpService()
    if (!http) {
      return this.handleError(new Error('HTTP服务未初始化'))
    }

    try {
      const response = await http.put(`${this.baseUrl}/group`, group)
      return this.handleResponse(response)
    } catch (err) {
      return this.handleError(err)
    }
  }

  async deleteEquipmentGroup(id: number | 0): Promise<ApiResponse<any>> {
    const http = this.httpService || this.getHttpService()
    if (!http) {
      return this.handleError(new Error('HTTP服务未初始化'))
    }

    try {
      const response = await http.delete(`${this.baseUrl}/group/${id}`)
      return this.handleResponse(response)
    } catch (err) {
      return this.handleError(err)
    }
  }
    //==================================================================================================================
    private getHttpService(): any {
        // 尝试从各个可能的位置获取 httpService
        const vue = (window as any).Vue
        if (vue && vue.prototype && vue.prototype.$httpservice) {
            return vue.prototype.$httpservice
        }
        return null
    }

    private handleResponse(response: any): ApiResponse {
        const data = response.data || response

      return {
            code: data.code || data.status,
            message: data.msg || '操作成功',
            data: data.data || data.result,
            success: data.code === 200,
            totalCount:data.totalCount
        }
    }

    private handleError(error: any): ApiResponse {
        return {
            code: error.code || -1,
            message: error.message || '请求失败',
            data: null,
            success: false,
            totalCount:0
        }
    }
}

export default EquipmentService



// WEBPACK FOOTER //
// ./src/service/EquipmentService.ts